import {
  adGroupDetailResponseSchema,
  type CampaignAiSetupPatchV1,
  CampaignAiSetupPatchV1Schema,
  type CampaignAiSetupResponseV1,
  CampaignAiSetupResponseV1Schema,
  type CampaignStateUpdate,
  type CampaignStateUpdateResponse,
  campaignAdGroupsResponseSchema,
  campaignBatchAiManagementResponseSchema,
  campaignBidAdjustmentsResponseSchema,
  campaignBudgetHistorySchema,
  campaignChangeHistoryResponseSchema,
  campaignDetailResponseSchema,
  campaignManagedGroupHistoryResponseSchema,
  campaignNegativesResponseSchema,
  campaignSearchTermsResponseSchema,
  campaignStateUpdateResponseSchema,
  campaignStateUpdateSchema,
  campaignTargetsResponseSchema,
  type PlacementKey,
  placementTrendResponseSchema,
  targetTagsUpdateSchema,
} from "@adpilot/shared";
import { type ReportingPeriod, reportingPeriodSearchParams } from "@/stores/reporting-period";
import { api } from "./api";
import type { ReportingTimeZone } from "./reporting-time-zone";

/** Amazon writes can outlive ky's 10s default while staying inside the server's bounded legs. */
export const CAMPAIGN_STATE_TIMEOUT_MS = 90_000;

export async function fetchCampaignDetail(
  id: string,
  range: ReportingPeriod,
  timeZone: ReportingTimeZone,
) {
  const res = await api
    .get(`campaigns/${id}`, { searchParams: reportingPeriodSearchParams(range, timeZone) })
    .json<{ success: true; data: unknown }>();
  return campaignDetailResponseSchema.parse(res.data);
}

/** ADP-222: per-day budget in force + changes for the Budget Tab trend. */
export async function fetchCampaignBudgetHistory(
  id: string,
  range: ReportingPeriod,
  timeZone: ReportingTimeZone,
) {
  const res = await api
    .get(`campaigns/${id}/budget-history`, {
      searchParams: reportingPeriodSearchParams(range, timeZone),
    })
    .json<{ success: true; data: unknown }>();
  return campaignBudgetHistorySchema.parse(res.data);
}

export async function fetchCampaignAdGroups(
  id: string,
  range: ReportingPeriod,
  timeZone: ReportingTimeZone,
  signal?: AbortSignal,
) {
  const res = await api
    .get(`campaigns/${id}/ad-groups`, {
      searchParams: reportingPeriodSearchParams(range, timeZone),
      signal,
    })
    .json<{ success: true; data: unknown }>();
  return campaignAdGroupsResponseSchema.parse(res.data);
}

export async function fetchCampaignTargets(
  id: string,
  range: ReportingPeriod,
  timeZone: ReportingTimeZone,
  signal?: AbortSignal,
) {
  const res = await api
    .get(`campaigns/${id}/targets`, {
      searchParams: reportingPeriodSearchParams(range, timeZone),
      signal,
    })
    .json<{ success: true; data: unknown }>();
  return campaignTargetsResponseSchema.parse(res.data);
}

export async function fetchCampaignSearchTerms(
  id: string,
  range: ReportingPeriod,
  timeZone: ReportingTimeZone,
  signal?: AbortSignal,
) {
  const res = await api
    .get(`campaigns/${id}/search-terms`, {
      searchParams: reportingPeriodSearchParams(range, timeZone),
      signal,
    })
    .json<{ success: true; data: unknown }>();
  return campaignSearchTermsResponseSchema.parse(res.data);
}

export async function fetchCampaignNegatives(id: string, signal?: AbortSignal) {
  const res = await api
    .get(`campaigns/${id}/negatives`, { signal })
    .json<{ success: true; data: unknown }>();
  return campaignNegativesResponseSchema.parse(res.data);
}

export async function fetchAdGroupDetail(
  id: string,
  adGroupId: string,
  range: ReportingPeriod,
  timeZone: ReportingTimeZone,
) {
  const res = await api
    .get(`campaigns/${id}/ad-groups/${adGroupId}`, {
      searchParams: reportingPeriodSearchParams(range, timeZone),
    })
    .json<{ success: true; data: unknown }>();
  return adGroupDetailResponseSchema.parse(res.data);
}

export async function updateTargetTags(targetId: string, tags: string[]) {
  const body = targetTagsUpdateSchema.parse({ tags });
  const res = await api
    .patch(`targets/${targetId}/tags`, { json: body })
    .json<{ success: true; data: unknown }>();
  return res.data;
}

export async function fetchCampaignAiSetup(id: string) {
  const res = await api.get(`campaigns/${id}/ai-setup`).json<{ success: true; data: unknown }>();
  return CampaignAiSetupResponseV1Schema.parse(res.data);
}

export async function updateCampaignAiSetup(
  id: string,
  patch: CampaignAiSetupPatchV1,
): Promise<CampaignAiSetupResponseV1> {
  const body = CampaignAiSetupPatchV1Schema.parse(patch);
  const res = await api
    .patch(`campaigns/${id}/ai-setup`, { json: body })
    .json<{ success: true; data: unknown }>();
  return CampaignAiSetupResponseV1Schema.parse(res.data);
}

export async function setCampaignsAiManagement(ids: string[], aiManaged: boolean) {
  const body = { campaign_ids: ids, ai_managed: aiManaged };
  const res = await api
    .patch("campaigns/batch/toggle", { json: body })
    .json<{ success: true; data: unknown }>();
  return campaignBatchAiManagementResponseSchema.parse(res.data);
}

export async function updateCampaignState(
  id: string,
  state: CampaignStateUpdate["state"],
): Promise<CampaignStateUpdateResponse> {
  const body = campaignStateUpdateSchema.parse({ state });
  const res = await api
    .patch(`campaigns/${id}/state`, {
      json: body,
      timeout: CAMPAIGN_STATE_TIMEOUT_MS,
    })
    .json<{ success: true; data: unknown }>();
  return campaignStateUpdateResponseSchema.parse(res.data);
}

export interface CampaignChangeHistoryParams {
  startDate: string;
  endDate: string;
  page: number;
  pageSize: number;
  objectTypes: readonly string[];
  operationTypes: readonly string[];
  operators: readonly string[];
  search: string;
  /** Zone the dates were picked in; the server cuts days and the UI reads times in it. */
  timeZone: string;
}

export async function fetchCampaignChangeHistory(
  id: string,
  params: CampaignChangeHistoryParams,
  signal?: AbortSignal,
) {
  const searchParams: Record<string, string | number> = {
    startDate: params.startDate,
    endDate: params.endDate,
    page: params.page,
    pageSize: params.pageSize,
    timeZone: params.timeZone,
  };
  if (params.objectTypes.length) searchParams.objectTypes = params.objectTypes.join(",");
  if (params.operationTypes.length) searchParams.operationTypes = params.operationTypes.join(",");
  if (params.operators.length) searchParams.operators = params.operators.join(",");
  if (params.search.trim()) searchParams.search = params.search.trim();
  const res = await api.get(`campaigns/${id}/change-history`, { searchParams, signal }).json();
  return campaignChangeHistoryResponseSchema.parse(res);
}

/** 竞价调整: the campaign's synced bidding beside each placement's performance. */
export async function fetchCampaignBidAdjustments(
  id: string,
  range: { startDate: string; endDate: string },
  signal?: AbortSignal,
) {
  const res = await api
    .get(`campaigns/${id}/bid-adjustments`, { searchParams: range, signal })
    .json();
  return campaignBidAdjustmentsResponseSchema.parse(res);
}

export async function fetchPlacementTrend(
  id: string,
  placement: PlacementKey,
  range: { startDate: string; endDate: string },
  signal?: AbortSignal,
) {
  const res = await api
    .get(`campaigns/${id}/bid-adjustments/placements/${placement}/trend`, {
      searchParams: range,
      signal,
    })
    .json();
  return placementTrendResponseSchema.parse(res);
}

export async function fetchCampaignManagedGroupHistory(
  id: string,
  params: { page: number; pageSize: number },
  signal?: AbortSignal,
) {
  const res = await api
    .get(`campaigns/${id}/managed-group-history`, { searchParams: params, signal })
    .json();
  return campaignManagedGroupHistoryResponseSchema.parse(res);
}
